import React, { useState, useEffect, useCallback, useContext } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import DashboardIcon from "@mui/icons-material/Dashboard";
import UndoIcon from "@mui/icons-material/Undo";
import DeleteIcon from "@mui/icons-material/Delete";
import SettingsIcon from "@mui/icons-material/Settings";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import SportsCricketIcon from '@mui/icons-material/SportsCricket';
import StadiumIcon from '@mui/icons-material/Stadium';
import LogoutIcon from '@mui/icons-material/Logout';
import CurrencyBitcoinIcon from '@mui/icons-material/CurrencyBitcoin';
import BlockIcon from '@mui/icons-material/Block';
import axios from "axios";
import { AuthContext } from "../context/AuthContext";
import api_url from "../constants";
import "../style/sidebar.css";

function Sidebar({ socket }) {
  const location = useLocation();
  const navigate = useNavigate();
  
  const [authContext, setAuthContext] = useContext(AuthContext);

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);

  const closeMobileSidebar = useCallback(() => {
    setIsMobileOpen(false);
  }, []);

  const logoutHandler = async () => {
    try {
      if (socket) {
        socket.disconnect();
      }

      await axios.get(api_url + "/auth/managerLogout", {
        withCredentials: true,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authContext.token}`,
        },
      });

      setAuthContext((oldValues) => {
        return { ...oldValues, details: undefined, token: null };
      });

      window.localStorage.setItem("managerLogout", Date.now());
      navigate("/");
    } catch (error) {
      console.error("Error during logout:", error);
      setAuthContext((oldValues) => ({ ...oldValues, details: undefined, token: null }));
      navigate("/");
    }
  };

  useEffect(() => {
    const handleResize = () => {
      const mobileView = window.innerWidth <= 1024;
      setIsMobile(mobileView);
      if (!mobileView) {
        setIsMobileOpen(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (isMobile && isMobileOpen) {
      document.body.classList.add("sidebar-open");
    } else {
      document.body.classList.remove("sidebar-open");
    }
  }, [isMobile, isMobileOpen]);

  const toggleSidebar = () => {
    if (isMobile) {
      setIsMobileOpen((prev) => !prev);
    } else {
      setIsCollapsed((prev) => !prev);
    }
  };

  const sidebarItems = [
    {
      path: "/",
      name: "Dashboard",
      icon: <DashboardIcon className="sidebar-icon-svg" />,
      description: "Main control panel",
    },
    {
      path: "/toss",
      name: "Toss",
      icon: <CurrencyBitcoinIcon className="sidebar-icon-svg" />,
      description: "Toss management",
    },
    {
      path: "/inplay",
      name: "In Play",
      icon: <SportsCricketIcon className="sidebar-icon-svg" />,
      description: "Active Matches",
    },
    {
      path: "/upcoming",
      name: "Upcoming",
      icon: <StadiumIcon className="sidebar-icon-svg" />,
      description: "Sheduled Matches",
    },
    {
      path: "/revert",
      name: "Revert",
      icon: <UndoIcon className="sidebar-icon-svg" />,
      description: "Revert operations",
    },
    {
      path: "/delete-bets",
      name: "Delete Bets",
      icon: <DeleteIcon className="sidebar-icon-svg" />,
      description: "Remove betting data",
    },
    {
      path: "/match-updates",
      name: "Match Updates",
      icon: <BlockIcon className="sidebar-icon-svg" />,
      description: "Block scheduled and active",
    },
    {
      path: "/settings",
      name: "Settings",
      icon: <SettingsIcon className="sidebar-icon-svg" />,
      description: "Application settings",
    },
  ];

  const showLabels = isMobile || !isCollapsed;

  return (
    <>
      <style>
        {`
          .logout-button {
            background: transparent;
            border: 1px solid rgba(255, 71, 87, 0.3); /* Visible Border */
            width: calc(100% - 24px);
            cursor: pointer;
            margin-top: 20px;
            text-align: left;
            transition: all 0.3s ease;
          }
          
          .logout-button:hover {
            background: rgba(255, 71, 87, 0.15) !important; /* Red hover effect */
            border-color: #ff4757;
            box-shadow: 0 0 8px rgba(255, 71, 87, 0.2);
          }

          .logout-icon-color {
            color: #ff4757;
            transition: transform 0.2s ease;
          }

          .logout-button:hover .logout-icon-color {
            transform: translateX(3px); /* Subtle animation on hover */
          }
        `}
      </style>

      {isMobile && isMobileOpen && (
        <div className="sidebar-overlay" onClick={closeMobileSidebar} />
      )}

      {isMobile && (
        <button className="mobile-sidebar-toggle" onClick={toggleSidebar}>
          {isMobileOpen ? <CloseIcon /> : <MenuIcon />}
        </button>
      )}

      <aside
        className={`modern-sidebar ${isCollapsed ? "collapsed" : ""} ${
          isMobile ? (isMobileOpen ? "mobile-open" : "mobile-closed") : ""
        }`}
      >
        <div className="sidebar-header">
          <div className="logo-section">
            {showLabels && (
              <div className="logo-text">
                <h3>Manager</h3>
                <span>Dashboard</span>
              </div>
            )}
          </div>
          {!isMobile && (
            <button className="sidebar-toggle" onClick={toggleSidebar}>
              {isCollapsed ? <MenuIcon /> : <CloseIcon />}
            </button>
          )}
        </div>

        <div className="sidebar-content">
          <nav className="sidebar-nav">
            <div className="nav-section">
              {sidebarItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`nav-item ${isActive ? "active" : ""}`}
                    onClick={closeMobileSidebar}
                  >
                    <div className="nav-icon">{item.icon}</div>
                    {showLabels && (
                      <div className="nav-content">
                        <span className="nav-text">{item.name}</span>
                        <span className="nav-description">
                          {item.description}
                        </span>
                      </div>
                    )}
                    {isActive && <div className="active-indicator" />}
                  </Link>
                );
              })}

              {/* LOGOUT BUTTON */}
              <button
                className="nav-item logout-button"
                onClick={logoutHandler}
              >
                <div className="nav-icon logout-icon-color">
                  <LogoutIcon className="sidebar-icon-svg" />
                </div>
                {showLabels && (
                  <div className="nav-content">
                    <span className="nav-text" style={{ color: "#ff4757" }}>
                      LOGOUT
                    </span>
                    <span 
                      className="nav-description" 
                      style={{ color: "#ff4757", opacity: 0.7 }}
                    >
                      End Session
                    </span>
                  </div>
                )}
              </button>
            </div>
          </nav>
        </div>
      </aside>
    </>
  );
}

export default Sidebar;